import React, { useEffect, useMemo, useState } from "react";
import { FiExternalLink, FiFile, FiUploadCloud } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Checkbox, Input, Select } from "components/common";
import { PageHeader, useToast } from "components/admin";
import { useActiveLanguagesQuery } from "hooks";
import { ASSET_BASE_URL } from "constants/apiEndpoints";
import {
  createShareholderDocument,
  getAdminShareholderDocument,
  getAdminShareholderDocumentCategories,
  updateShareholderDocument,
  uploadShareholderDocument,
} from "apis/shareholderDocuments";
import "../../../styles/admin-shared.scss";
import "./shareholderDocuments.scss";

const createInitialForm = (languages = []) => ({
  categoryId: "",
  fileUrl: "",
  fileName: "",
  mimeType: "",
  publishedYear: new Date().getFullYear(),
  sortOrder: 0,
  isActive: true,
  translation: languages.map((language) => ({
    languageId: Number(language.id),
    title: "",
    description: "",
  })),
});

const normalizeDetail = (data, languages) => {
  const source = data?.item ?? data?.data ?? data ?? {};
  const translations = source.translation ?? source.translations ?? source.Translation ?? [];
  return {
    categoryId: source.categoryId ?? source.CategoryId ?? "",
    fileUrl: source.fileUrl ?? source.FileUrl ?? "",
    fileName: source.fileName ?? source.FileName ?? "",
    mimeType: source.mimeType ?? source.MimeType ?? "",
    publishedYear: source.publishedYear ?? source.PublishedYear ?? "",
    sortOrder: source.sortOrder ?? source.SortOrder ?? 0,
    isActive: source.isActive ?? source.IsActive ?? true,
    translation: languages.map((language) => {
      const current = translations.find(
        (item) =>
          Number(item.languageId ?? item.LanguageId) === Number(language.id),
      );
      return {
        ...current,
        languageId: Number(language.id),
        title: current?.title ?? current?.Title ?? "",
        description: current?.description ?? current?.Description ?? "",
      };
    }),
  };
};

const resolveDocumentUrl = (url) => {
  if (!url || /^(https?:)?\/\//i.test(url)) return url;
  return `${ASSET_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
};

export default function ShareholderDocumentForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: languages = [] } = useActiveLanguagesQuery();
  const [form, setForm] = useState(createInitialForm());
  const [categories, setCategories] = useState([]);
  const [activeLanguageId, setActiveLanguageId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(Boolean(id));

  useEffect(() => {
    if (!languages.length) return;
    setActiveLanguageId((current) => current ?? Number(languages[0].id));
    if (!id) setForm(createInitialForm(languages));
  }, [id, languages]);

  useEffect(() => {
    getAdminShareholderDocumentCategories({
      languageCode: "vi",
      pageIndex: 1,
      pageSize: 100,
    })
      .then((data) =>
        setCategories(
          Array.isArray(data) ? data : data?.items ?? data?.data ?? [],
        ),
      )
      .catch(() => toast.error("Không thể tải danh mục tài liệu."));
  }, [toast]);

  useEffect(() => {
    if (!id || !languages.length) return;
    setLoading(true);
    getAdminShareholderDocument(id)
      .then((data) => setForm(normalizeDetail(data, languages)))
      .catch(() => toast.error("Không thể tải thông tin tài liệu."))
      .finally(() => setLoading(false));
  }, [id, languages, toast]);

  const activeTranslation = useMemo(
    () =>
      form.translation.find(
        (item) => Number(item.languageId) === Number(activeLanguageId),
      ) ?? {},
    [activeLanguageId, form.translation],
  );

  const patchTranslation = (field, value) =>
    setForm((current) => ({
      ...current,
      translation: current.translation.map((item) =>
        Number(item.languageId) === Number(activeLanguageId)
          ? { ...item, [field]: value }
          : item,
      ),
    }));

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadShareholderDocument(file);
      setForm((current) => ({
        ...current,
        fileUrl: uploaded.fileUrl,
        fileName: uploaded.originalName ?? uploaded.fileName ?? file.name,
        mimeType: uploaded.mimeType ?? file.type,
      }));
      toast.success("Tải tài liệu thành công.");
    } catch {
      toast.error("Không thể tải tài liệu. Vui lòng chọn PDF, DOC hoặc DOCX tối đa 20 MB.");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      categoryId: Number(form.categoryId),
      publishedYear: form.publishedYear ? Number(form.publishedYear) : null,
      sortOrder: Number(form.sortOrder) || 0,
      translation: form.translation.map((item) => ({
        languageId: Number(item.languageId),
        title: item.title?.trim() ?? "",
        description: item.description?.trim() ?? "",
      })),
    };
    try {
      await (id
        ? updateShareholderDocument(id, payload)
        : createShareholderDocument(payload));
      toast.success("Đã lưu tài liệu cổ đông.");
      navigate("/admin/shareholder-documents");
    } catch {
      toast.error("Lưu tài liệu thất bại. Vui lòng kiểm tra lại dữ liệu.");
    }
  };

  if (loading) {
    return <div className="admin-page"><div className="document-form-skeleton" /></div>;
  }

  return (
    <div className="admin-page">
      <PageHeader
        title={id ? "Chỉnh sửa tài liệu" : "Thêm tài liệu cổ đông"}
        subtitle="Đăng PDF, Word từ máy hoặc sử dụng đường dẫn tài liệu bên ngoài."
      />
      <form className="form-card shareholder-document-form" onSubmit={submit}>
        {languages.length > 1 && (
          <div className="document-language-tabs">
            {languages.map((language) => (
              <button
                type="button"
                className={Number(activeLanguageId) === Number(language.id) ? "active" : ""}
                key={language.id}
                onClick={() => setActiveLanguageId(Number(language.id))}
              >
                {language.name || language.code}
              </button>
            ))}
          </div>
        )}

        <div className="form-grid">
          <Input
            label="Tiêu đề *"
            value={activeTranslation.title || ""}
            onChange={(event) => patchTranslation("title", event.target.value)}
            required
          />
          <Select
            label="Danh mục *"
            value={form.categoryId || ""}
            onChange={(event) =>
              setForm((current) => ({ ...current, categoryId: event.target.value }))
            }
            options={[
              { value: "", label: "Chọn danh mục" },
              ...categories.map((category) => ({
                value: category.id ?? category.Id,
                label:
                  category.name ??
                  category.Name ??
                  category.title ??
                  category.code ??
                  category.Code,
              })),
            ]}
            required
          />
          <Input
            label="Năm công bố"
            type="number"
            min="1900"
            max="2200"
            value={form.publishedYear || ""}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                publishedYear: event.target.value,
              }))
            }
          />
          <Input
            label="Thứ tự hiển thị"
            type="number"
            min="0"
            value={form.sortOrder ?? 0}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                sortOrder: Number(event.target.value),
              }))
            }
          />
        </div>

        <label className={`document-dropzone ${form.fileName ? "has-file" : ""}`}>
          <span className="document-dropzone__icon">
            {form.fileName ? <FiFile /> : <FiUploadCloud />}
          </span>
          <div>
            <strong>
              {uploading
                ? "Đang tải lên..."
                : form.fileName || "Kéo thả hoặc chọn tài liệu"}
            </strong>
            <span>PDF, DOC, DOCX · tối đa 20 MB</span>
          </div>
          <em>{form.fileName ? "Thay tệp" : "Chọn tệp"}</em>
          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={chooseFile}
            disabled={uploading}
          />
        </label>

        <div className="document-url-panel">
          <FiExternalLink />
          <Input
            label="URL tài liệu *"
            value={form.fileUrl || ""}
            onChange={(event) =>
              setForm((current) => ({ ...current, fileUrl: event.target.value }))
            }
            placeholder="https://... hoặc /uploads/..."
            required
          />
        </div>

        <label className="admin-textarea">
          <span>Mô tả</span>
          <textarea
            rows="5"
            value={activeTranslation.description || ""}
            onChange={(event) =>
              patchTranslation("description", event.target.value)
            }
            placeholder="Mô tả ngắn nội dung và kỳ báo cáo..."
          />
        </label>

        {form.fileUrl && (
          <a
            className="document-preview"
            href={resolveDocumentUrl(form.fileUrl)}
            target="_blank"
            rel="noreferrer"
          >
            <FiFile /> Mở tài liệu để kiểm tra
            <FiExternalLink />
          </a>
        )}

        <Checkbox
          label="Hiển thị trên website"
          checked={Boolean(form.isActive)}
          onChange={(event) =>
            setForm((current) => ({
              ...current,
              isActive: event.target.checked,
            }))
          }
        />
        <div className="form-actions">
          <Button type="submit">Lưu tài liệu</Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/shareholder-documents")}
          >
            Hủy
          </Button>
        </div>
      </form>
    </div>
  );
}
